import { useEffect, useState } from 'react';
import type { BootstrapState } from '@/app/bootstrap/types';
import { resolveRole } from '@/shared/roles/resolveRole';
import {
  API_BASE_URL,
  fetchShowcaseData,
  getShowcaseBlockIdFromUrl,
} from '@/shared/api/showcaseApi';
import { apiFetch, authenticateShowcase } from '@/shared/auth/runtimeAuth';
import { isTrustedPreviewRuntime } from '@/shared/env/runtimeMode';
import { updateStartupLoader } from '@/shared/bootstrap/startupLoader';

const BOOTSTRAP_TIMEOUT_MS = 15_000;

function getRoleFromQuery(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('role') ?? params.get('mockRole');
}

function getRoleFromPathname(): string | null {
  const pathname = window.location.pathname.toLowerCase();

  if (pathname === '/manager' || pathname.startsWith('/manager/')) {
    return 'manager';
  }

  if (pathname === '/specialist' || pathname.startsWith('/specialist/')) {
    return 'specialist';
  }

  if (
    pathname === '/' ||
    pathname === '/show-case' ||
    pathname.startsWith('/show-case/')
  ) {
    return 'client';
  }

  return null;
}

function getRoleFromTelegramStartParam(): string | null {
  const startParam = (window.Telegram?.WebApp?.initDataUnsafe as { start_param?: string } | undefined)
    ?.start_param;
  if (!startParam) return null;

  if (startParam.includes('role=')) {
    const match = startParam.match(/role=([a-zA-Z_-]+)/);
    return match?.[1] ?? null;
  }

  return startParam;
}

export function useAppBootstrap(): BootstrapState {
  const [state, setState] = useState<BootstrapState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let didTimeout = false;
    const timeoutId = window.setTimeout(() => {
      didTimeout = true;
      controller.abort();
      setState({
        status: 'error',
        message: 'Витрина не успела загрузиться. Проверьте соединение и попробуйте ещё раз',
      });
    }, BOOTSTRAP_TIMEOUT_MS);

    const bootstrap = async () => {
      try {
        updateStartupLoader(0);
        const previewRole = isTrustedPreviewRuntime
          ? getRoleFromQuery() || getRoleFromPathname() || getRoleFromTelegramStartParam()
          : null;
        const blockId = getShowcaseBlockIdFromUrl();

        if (!blockId) {
          if (isTrustedPreviewRuntime) {
            setState({
              status: 'ready',
              role: resolveRole({ queryRole: previewRole, fallbackRole: 'client' }),
              workflowSettings: {},
            });
            return;
          }
          setState({ status: 'error', message: 'Откройте витрину из подключённого бота' });
          return;
        }

        updateStartupLoader(1);
        const block = await fetchShowcaseData(blockId, controller.signal);
        if (didTimeout) return;
        if (isTrustedPreviewRuntime && previewRole) {
          setState({
            status: 'ready',
            role: resolveRole({ queryRole: previewRole, fallbackRole: 'client' }),
            workflowSettings: block.workflow_settings || {},
          });
          return;
        }
        updateStartupLoader(2);
        await authenticateShowcase(block.project_id, controller.signal);
        if (didTimeout) return;
        updateStartupLoader(3);
        const roleResponse = await apiFetch(
          `${API_BASE_URL}/showcase/showcase-blocks/${blockId}/role`,
          { signal: controller.signal },
        );
        if (!roleResponse.ok) {
          throw new Error(`Не удалось определить роль: HTTP ${roleResponse.status}`);
        }
        const rolePayload = (await roleResponse.json()) as { role?: string };
        if (didTimeout) return;
        const role = resolveRole({
          queryRole: previewRole,
          startParamRole: rolePayload.role || null,
          fallbackRole: 'client',
        });

        updateStartupLoader(4);
        setState({
          status: 'ready',
          role,
          workflowSettings: block.workflow_settings || {},
        });
        window.clearTimeout(timeoutId);
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
        window.clearTimeout(timeoutId);
        console.error('Showcase bootstrap failed');
        const requiresTrustedLaunch = (error as Error).message.includes(
          'данные запуска Telegram или MAX',
        );
        setState({
          status: 'error',
          message: requiresTrustedLaunch
            ? 'Откройте витрину из подключённого бота'
            : 'Не удалось загрузить витрину. Проверьте интернет и попробуйте ещё раз',
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    void bootstrap();
    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  return state;
}
